import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Progress,
  Row,
  Tooltip,
  Select,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  CheckCircleOutlined,
  CloudUploadOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import AppBreadcrumb from '../../components/Breadcrumb';
import { bulkInsertTestSteps, getCTModules, validateTestSteps } from '../../services/testingApi';
import { getSelectedProject } from '../../services/authApi';
import '../../styles/test-page.css';

const { Text, Title } = Typography;

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const TEMPLATE_BASE64_URL = '/templates/test_cases_flow_template.base64.txt';
const TEMPLATE_FILENAME = 'test_cases_flow_template.xlsx';
const HEADERS_HINT = 'module | subModule | step_order | description | actions | key | value | expected | scenario';
const STEP_INDEX = {
  idle: 0,
  module_set: 1,
  encoding: 1,
  validating: 2,
  preview: 3,
  inserting: 3,
  done: 4,
  error: 1,
};

const breadcrumbItems = [
  { label: 'Home', href: '/' },
  { label: 'Test Cases', href: '/addTestCase' },
  { label: 'Bulk upload', href: '/bulk-upload' },
];

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function cleanRowsForInsert(rows) {
  return rows
    .filter((row) => row.row_status === 'valid')
    .map((row) => {
      const cleanRow = { ...row };
      delete cleanRow.row_status;
      delete cleanRow.errors;
      return cleanRow;
    });
}

function collectErrors(rows) {
  return rows.flatMap((row) =>
    (row.errors ?? []).map((error) => ({
      id: row.id,
      ...error,
    })),
  );
}

function ErrorTooltipContent({ errors }) {
  return (
    <div className="bulk-tooltip-errors">
      {(errors ?? []).map((error, index) => (
        <div key={`${error.field}-${index}`}>{error.message}</div>
      ))}
    </div>
  );
}

function BulkErrorBanner({ errors, expanded, onToggle, onSummaryClick }) {
  if (errors.length === 0) return null;

  return (
    <div className="bulk-error-banner">
      <div className="bulk-error-summary">
        <button
          type="button"
          className="bulk-error-summary-main"
          onClick={onSummaryClick}
          aria-label="Scroll to first invalid row"
        >
          <ExclamationCircleOutlined className="bulk-error-icon" />
          <span>{errors.length} validation error(s) found</span>
        </button>
        <Button type="link" onClick={onToggle} aria-expanded={expanded}>
          {expanded ? 'Hide Errors' : 'View Errors'}
        </Button>
      </div>
      {expanded && (
        <div className="bulk-error-list" role="region" aria-label="Validation error details">
          {errors.map((error, index) => (
            <div key={`${error.id}-${error.field}-${index}`} className="bulk-error-line">
              <span className="bulk-row-chip">Row {error.id}</span>
              <span>{error.message}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RowPreviewTable({ rows, view, onViewChange, onConfirm, onReset, saving, firstInvalidRowId, firstInvalidRowRef }) {
  const invalidRows = rows.filter((row) => row.row_status === 'invalid');
  const visibleRows = view === 'errors' ? invalidRows : rows;
  const disableSave = invalidRows.length > 0 || saving;

  return (
    <>
      <Row justify="space-between" align="middle" gutter={[16, 16]} className="bulk-table-toolbar">
        <Col>
          <Space.Compact>
            <Button type={view === 'all' ? 'primary' : 'default'} onClick={() => onViewChange('all')}>
              All rows
            </Button>
            <Button type={view === 'errors' ? 'primary' : 'default'} onClick={() => onViewChange('errors')}>
              Errors only
            </Button>
          </Space.Compact>
        </Col>
        <Col>
          <Text type="secondary">{rows.length} row(s)</Text>
        </Col>
      </Row>

      <div className="bulk-table-wrap">
        <table className="bulk-preview-table">
          <thead>
            <tr>
              <th className="bulk-col-row">Row</th>
              <th className="bulk-col-flow">Submodule</th>
              <th className="bulk-col-description">Description</th>
              <th className="bulk-col-keyword">Action</th>
              <th className="bulk-col-target">Key</th>
              <th className="bulk-col-status">Status</th>
              <th className="bulk-col-error">Error</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row) => {
              const rowErrors = row.errors ?? [];
              const isInvalid = row.row_status === 'invalid';
              return (
                <tr
                  key={`${row.id}-${row.step_order}`}
                  ref={isInvalid && row.id === firstInvalidRowId ? firstInvalidRowRef : null}
                  className={isInvalid ? 'bulk-row-invalid' : ''}
                >
                  <td>{row.id}</td>
                  <td title={row.submodule}>{row.submodule}</td>
                  <td title={row.description}>
                    {row.description ? row.description : <span className="bulk-empty-text">-empty-</span>}
                  </td>
                  <td><code className="bulk-code-chip">{row.actions}</code></td>
                  <td title={row.key}>{row.key}</td>
                  <td>
                    {isInvalid
                      ? <Tag color="red">Error</Tag>
                      : <Tag color="green">Valid</Tag>}
                  </td>
                  <td>
                    {isInvalid ? (
                      <Tooltip title={<ErrorTooltipContent errors={rowErrors} />} placement="left">
                        <button type="button" className="bulk-error-info" aria-label={`View ${rowErrors.length} error(s) for row ${row.id}`}>
                          <InfoCircleOutlined />
                          <Badge count={rowErrors.length} size="small" />
                        </button>
                      </Tooltip>
                    ) : (
                      <span className="bulk-error-none">-</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Space className="bulk-actions">
        <Button icon={<ReloadOutlined />} onClick={onReset}>Reset</Button>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          loading={saving}
          disabled={disableSave}
          onClick={onConfirm}
        >
          {invalidRows.length > 0 ? `Fix ${invalidRows.length} error(s) to proceed` : 'Save to database'}
        </Button>
      </Space>
    </>
  );
}

export default function BulkUploadPage() {
  const navigate = useNavigate();
  const project = useMemo(() => getSelectedProject(), []);
  const fileInputRef = useRef(null);
  const firstInvalidRowRef = useRef(null);
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState('');
  const [status, setStatus] = useState('idle');
  const [dragging, setDragging] = useState(false);
  const [fileInfo, setFileInfo] = useState(null);
  const [rows, setRows] = useState([]);
  const [view, setView] = useState('all');
  const [apiError, setApiError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [errorsExpanded, setErrorsExpanded] = useState(false);

  const moduleOptions = modules.map((item) => ({ value: item.modulename, label: item.modulename }));
  const errors = collectErrors(rows);
  const busy = status === 'encoding' || status === 'validating';
  const canChooseFile = Boolean(selectedModule) && !busy;
  const activeStep = STEP_INDEX[status] ?? 0;
  const firstInvalidRowId = rows.find((row) => row.row_status === 'invalid')?.id;

  const reset = useCallback(() => {
    setSelectedModule('');
    setStatus('idle');
    setDragging(false);
    setFileInfo(null);
    setRows([]);
    setView('all');
    setApiError(null);
    setSummary(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  useEffect(() => {
    async function loadModules() {
      try {
        const res = await getCTModules({ project: project?.name ?? '', activeOnly: true });
        setModules(res?.items ?? []);
      } catch (error) {
        console.error(error);
        message.error('Unable to load modules.');
      }
    }
    loadModules();
  }, [project?.name]);

  const handleModuleChange = (value) => {
    setSelectedModule(value ?? '');
    setRows([]);
    setSummary(null);
    setApiError(null);
    setFileInfo(null);
    setErrorsExpanded(false);
    setStatus(value ? 'module_set' : 'idle');
  };

  const validateFile = async (file) => {
    if (!file || !canChooseFile) return;

    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!['xlsx', 'csv'].includes(extension)) {
      setStatus('module_set');
      setApiError({ message: 'Invalid file type. Please upload an Excel .xlsx or CSV .csv file.' });
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setStatus('module_set');
      setApiError({ message: 'File must be 10 MB or smaller.' });
      return;
    }

    setApiError(null);
    setRows([]);
    setSummary(null);
    setFileInfo({ name: file.name, size: file.size });
    setStatus('encoding');

    try {
      const fileBase64 = await fileToBase64(file);
      setStatus('validating');
      const data = await validateTestSteps({
        filename: file.name,
        project: project?.name ?? '',
        module: selectedModule,
        file_base64: fileBase64,
        file_type: extension,
      });
      setRows(data?.rows ?? []);
      setView('all');
      setStatus('preview');
    } catch (error) {
      console.error(error);
      setApiError(error.payload ?? { code: 'NETWORK_ERROR', message: error.message || 'Could not reach the server.' });
      setStatus('error');
    }
  };

  const handleTemplateDownload = async () => {
    try {
      const response = await fetch(TEMPLATE_BASE64_URL);
      if (!response.ok) throw new Error(`Template file not found (${response.status})`);

      const base64 = (await response.text()).trim();
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let index = 0; index < binary.length; index += 1) {
        bytes[index] = binary.charCodeAt(index);
      }

      const blob = new Blob([bytes], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = TEMPLATE_FILENAME;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      message.success('Sample template downloaded.');
    } catch (error) {
      console.error(error);
      message.error('Unable to download sample template.');
    }
  };

  const confirmInsert = async () => {
    setStatus('inserting');
    setApiError(null);
    try {
      const data = await bulkInsertTestSteps({
        project: project?.name ?? '',
        module: selectedModule,
        uploaded_by: localStorage.getItem('user_Email') ?? '',
        rows: cleanRowsForInsert(rows),
      });
      setSummary(data);
      setStatus('done');
      message.success('Bulk upload saved. Redirecting to view test cases...');
      window.setTimeout(() => navigate('/viewTestCase'), 900);
    } catch (error) {
      console.error(error);
      setApiError(error.payload ?? { code: 'NETWORK_ERROR', message: error.message || 'Could not reach the server.' });
      setStatus('preview');
    }
  };

  const scrollToFirstInvalidRow = useCallback(() => {
    firstInvalidRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  const dropzoneClass = [
    'bulk-dropzone',
    dragging ? 'bulk-dropzone-dragging' : '',
    fileInfo ? 'bulk-dropzone-has-file' : '',
    apiError && status !== 'preview' ? 'bulk-dropzone-error' : '',
    !selectedModule ? 'bulk-dropzone-disabled' : '',
  ].filter(Boolean).join(' ');

  return (
    <main className="jobs-list-page">
      <Row align="middle" justify="space-between" className="breadcrumb-action-wrapper">
        <Col><AppBreadcrumb items={breadcrumbItems} /></Col>
        {project?.name && (
          <Col><Text strong>{project.name}</Text></Col>
        )}
      </Row>

      <div className="zt-page zt-page-wide bulk-page">
        <div className="bulk-header">
          <Title level={3}>Bulk upload test cases</Title>
          <Space direction="vertical" size={4}>
            <Text type="secondary">{HEADERS_HINT}</Text>
            <Text type="secondary">Use the sample template for the required columns. Uploaded rows are saved as Active by default.</Text>
          </Space>
        </div>

        <div className="bulk-stepbar">
          {['Select', 'Upload', 'Validate', 'Save'].map((label, index) => (
            <span
              key={label}
              className={index < activeStep ? 'bulk-step-active' : ''}
            >
              {index + 1} {label}
            </span>
          ))}
        </div>

        <Card className="v1-card zt-fall" title="1. Module selector">
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            className="bulk-module-select"
            placeholder="Choose a module"
            options={moduleOptions}
            value={selectedModule || undefined}
            onChange={handleModuleChange}
          />
        </Card>

        {selectedModule && (
          <Card
            className="v1-card zt-fall"
            title="2. File upload"
            extra={(
              <Button
                icon={<DownloadOutlined />}
                onClick={handleTemplateDownload}
              >
                Download Sample Template
              </Button>
            )}
          >
            <Alert
              type="info"
              showIcon
              message="First time uploading?"
              description="Download the sample template, keep the column names unchanged, fill your test steps, and upload either .xlsx or .csv."
              className="bulk-inline-alert"
            />
            <button
              type="button"
              className={dropzoneClass}
              disabled={!canChooseFile}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(event) => {
                event.preventDefault();
                if (canChooseFile) setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(event) => {
                event.preventDefault();
                setDragging(false);
                validateFile(event.dataTransfer.files?.[0]);
              }}
            >
              <CloudUploadOutlined className="bulk-upload-icon" />
              {fileInfo ? (
                <span className="bulk-file-chip">
                  {fileInfo.name} - {Math.ceil(fileInfo.size / 1024)} KB
                </span>
              ) : (
                <span>Drag &amp; drop here, or click to browse</span>
              )}
            </button>
            <input
              ref={fileInputRef}
              hidden
              type="file"
              accept=".xlsx,.csv"
              onChange={(event) => validateFile(event.target.files?.[0])}
            />
            {busy && <Progress percent={status === 'encoding' ? 45 : 82} showInfo={false} size="small" />}
            {apiError && status !== 'preview' && <Alert type="error" showIcon message={apiError.message} className="bulk-inline-alert" />}
          </Card>
        )}

        {rows.length > 0 && (
          <Card className="v1-card zt-fall" title="3. Validation result">
            <BulkErrorBanner
              errors={errors}
              expanded={errorsExpanded}
              onToggle={() => setErrorsExpanded((value) => !value)}
              onSummaryClick={scrollToFirstInvalidRow}
            />
            {apiError && status === 'preview' && <Alert type="error" showIcon message={apiError.message} className="bulk-inline-alert" />}
            <RowPreviewTable
              rows={rows}
              view={view}
              onViewChange={setView}
              onConfirm={confirmInsert}
              onReset={reset}
              saving={status === 'inserting'}
              firstInvalidRowId={firstInvalidRowId}
              firstInvalidRowRef={firstInvalidRowRef}
            />
          </Card>
        )}

        {summary && status === 'done' && (
          <Card className="v1-card zt-fall" title="4. Upload complete">
            <Row gutter={[16, 16]} className="bulk-metrics">
              <Col xs={24} md={8}><div><Text>Total</Text><strong>{rows.length}</strong></div></Col>
              <Col xs={24} md={8}><div><Text>Inserted</Text><strong>{summary.inserted ?? 0}</strong></div></Col>
              <Col xs={24} md={8}><div><Text>Failed</Text><strong>{summary.failed ?? 0}</strong></div></Col>
            </Row>
            <Alert
              type="success"
              showIcon
              icon={<CheckCircleOutlined />}
              message={`${summary.inserted ?? 0} test cases saved to database - module: ${selectedModule}`}
              className="bulk-inline-alert"
            />
            <Button icon={<ReloadOutlined />} onClick={reset}>Upload another file</Button>
          </Card>
        )}
      </div>
    </main>
  );
}







